import React, { useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { capitalize, isEmpty, xor } from 'lodash'
import { IconCheck, IconX } from '@tabler/icons-react'

import { fetchCSVExport, preFetchCSVExport } from '@/reducers/analytics/analytics.actions'
import { Button } from '@/components/buttons/Button'
import { Radio } from '@/components/forms/Checkbox'
import { Card } from '@/components/cards/Card'
import { FormField } from '@/components/forms/FormField'

import { CSV_EXPORT_SECTIONS } from '../analytics.constants'

const { CHECKLIST, NOTIFICATIONS, POSTCALL, DYNAMIC_PROMPT } = CSV_EXPORT_SECTIONS

export const ExportCSVSection = () => {
  const dispatch = useDispatch()
  const {
    loading,
    filters: { playbooks },
    data: { callsDownloaded },
  } = useSelector((state) => state.analytics)
  const [disabledSections, setDisabledSections] = useState([])
  const [reportType, setReportType] = useState('call_log')
  const allowedSections = [CHECKLIST, NOTIFICATIONS, POSTCALL, DYNAMIC_PROMPT]
  const allowedSectionsSummary = [CHECKLIST, NOTIFICATIONS]
  const radioOptions = [
    { label: 'Call Log', value: 'call_log' },
    { label: 'Summary', value: 'summary' },
  ]
  const noSectionsSelected =
    reportType === 'call_log'
      ? isEmpty(xor(allowedSections, disabledSections))
      : isEmpty(xor(allowedSectionsSummary, disabledSections))

  const handleChangeReportType = (event, { value }) => {
    setReportType(value)
  }

  const handleToggleSection = (section) => {
    setDisabledSections((prevState) => {
      return prevState.includes(section)
        ? prevState.filter((disabledSection) => disabledSection !== section)
        : prevState.concat(section)
    })
  }

  const getSelectedSections = () => {
    return reportType === 'call_log'
      ? allowedSections.filter((section) => !disabledSections.includes(section))
      : allowedSectionsSummary.filter((section) => !disabledSections.includes(section))
  }

  const handleExport = async () => {
    const selectedSections = getSelectedSections()

    const playbook = await dispatch(preFetchCSVExport())

    if (playbook) {
      dispatch(fetchCSVExport(reportType, selectedSections, playbook))
    }
  }

  const getSectionName = (section) => {
    if (section === POSTCALL) return 'Post Call'
    if (section === DYNAMIC_PROMPT) return 'Dynamic Prompt'

    return capitalize(section)
  }

  const isDisabled = () => {
    if (noSectionsSelected) return true
    if (playbooks.length !== 1) return true

    return false
  }

  return (
    <div data-testid="reports-csv-exports">
      <Card>
        <h3>Export CSV</h3>
        <div className="field-stack">
          <div className="flex-align-center gap">
            <FormField>
              <label>Report Type</label>
            </FormField>
            {radioOptions.map((radioOption) => (
              <FormField key={radioOption.value}>
                <Radio
                  label={radioOption.label}
                  name="reportType"
                  value={radioOption.value}
                  checked={reportType === radioOption.value}
                  onChange={handleChangeReportType}
                />
              </FormField>
            ))}
          </div>
          <div className="flex-align-center small-gap">
            {allowedSections
              .filter((section) => {
                // if reportType is summary we don't want classified_postcall/deck included
                // because we haven't decided what customers want in summary for them
                if (allowedSections.includes(section) && reportType !== 'summary') {
                  return true
                }
                if (allowedSectionsSummary.includes(section) && reportType === 'summary') {
                  return true
                }

                return false
              })
              .map((section) => (
                <Button
                  small
                  success={!disabledSections.includes(section)}
                  key={section}
                  onClick={() => handleToggleSection(section)}
                >
                  {disabledSections.includes(section) ? (
                    <IconX className="icon-svg status-critical" />
                  ) : (
                    <IconCheck className="icon-svg" />
                  )}
                  {getSectionName(section)}
                </Button>
              ))}
          </div>
          <div className="flex-align-center gap">
            <Button
              data-testid="export-csv-button"
              brand
              loading={loading.exportCSVPage}
              onClick={handleExport}
              disabled={isDisabled()}
              disabledTooltip="Select one playbook"
            >
              Export CSV
            </Button>
            <div>
              <strong>Downloading Calls: </strong>
              <span>{callsDownloaded}</span>
            </div>
          </div>
        </div>
      </Card>
    </div>
  )
}
